import React, { useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { useMap } from '../../context/MapContext';
import './MapHeader.css';

const FORMATS = [
  { label: 'GeoJSON', outputFormat: 'application/json', ext: 'geojson' },
  { label: 'CSV', outputFormat: 'csv', ext: 'csv' },
  { label: 'KML', outputFormat: 'application/vnd.google-earth.kml+xml', ext: 'kml' },
  { label: 'Shape', outputFormat: 'SHAPE-ZIP', ext: 'zip' },
  { label: 'XLSX', outputFormat: '__xlsx__', ext: 'xlsx' },
];

const VISUAL_EXPORTS = [
  { label: 'Capturar', type: 'image' },
];

function wfsUrl(layer, outputFormat) {
  const base = layer.baseUrl.replace('/wms', '/wfs');
  return (
    `${base}?service=WFS&request=GetFeature` +
    `&typeName=${layer.layers}` +
    `&outputFormat=${encodeURIComponent(outputFormat)}`
  );
}

function safeFileName(name) {
  return name.replace(/[^a-zA-Z0-9_\-. ]/g, '_').trim();
}

function downloadFile(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function captureMapSnapshot(filenameBase) {
  const mapElement = document.querySelector('.mapview-container');
  if (!mapElement) {
    throw new Error('Nao foi possivel localizar o mapa para capturar a imagem.');
  }

  const canvas = await html2canvas(mapElement, {
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#ffffff',
    logging: false,
    scale: Math.min(window.devicePixelRatio || 1, 2),
  });

  return {
    dataUrl: canvas.toDataURL('image/png'),
    filenameBase,
  };
}

function printImageAsPdf(imageUrl, title) {
  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=900');
  if (!printWindow) {
    throw new Error('Permita pop-ups para salvar o mapa em PDF.');
  }

  const escapedTitle = title.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  printWindow.document.write(`
    <!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="utf-8" />
        <title>${escapedTitle}</title>
        <style>
          body {
            margin: 0;
            font-family: Arial, sans-serif;
            background: #ffffff;
            color: #0f172a;
          }
          .sheet {
            padding: 24px;
          }
          h1 {
            margin: 0 0 12px;
            font-size: 18px;
          }
          img {
            display: block;
            width: 100%;
            height: auto;
            border: 1px solid #cbd5e1;
          }
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <h1>${escapedTitle}</h1>
          <img src="${imageUrl}" alt="${escapedTitle}" />
        </div>
        <script>
          const image = document.querySelector('img');
          image.addEventListener('load', () => {
            window.focus();
            window.print();
          });
          image.addEventListener('error', () => {
            document.body.innerHTML = '<p style="padding:24px;font-family:Arial,sans-serif;">Nao foi possivel gerar a visualizacao para PDF.</p>';
          });
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}

async function downloadXlsx(layers, setLoading) {
  setLoading('XLSX');
  try {
    const wb = XLSX.utils.book_new();
    await Promise.all(
      layers.map(async (layer) => {
        const proxyUrl = `/api/wms-proxy?url=${encodeURIComponent(wfsUrl(layer, 'application/json'))}`;
        const res = await fetch(proxyUrl);
        if (!res.ok) throw new Error(`Falha ao baixar ${layer.name}`);
        const json = await res.json();
        const rows = (json.features || []).map((f) => f.properties || {});
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, safeFileName(layer.name).slice(0, 31));
      })
    );
    XLSX.writeFile(
      wb,
      layers.length === 1 ? `${safeFileName(layers[0].name)}.xlsx` : 'camadas.xlsx'
    );
  } catch (err) {
    console.warn('XLSX download failed:', err);
    alert(`Erro ao gerar XLSX: ${err.message}`);
  } finally {
    setLoading(null);
  }
}

function downloadDirect(layer, fmt) {
  const url = `/api/wms-proxy?url=${encodeURIComponent(wfsUrl(layer, fmt.outputFormat))}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFileName(layer.name)}.${fmt.ext}`;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function downloadZipped(layers, fmt, setLoading) {
  setLoading(fmt.label);
  const zip = new JSZip();
  try {
    await Promise.all(
      layers.map(async (layer) => {
        const proxyUrl = `/api/wms-proxy?url=${encodeURIComponent(wfsUrl(layer, fmt.outputFormat))}`;
        const res = await fetch(proxyUrl);
        if (!res.ok) throw new Error(`Falha ao baixar ${layer.name}`);
        const blob = await res.blob();
        zip.file(`${safeFileName(layer.name)}.${fmt.ext}`, blob);
      })
    );
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `camadas_${fmt.label.toLowerCase()}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.warn('Download zip failed:', err);
    alert(`Erro ao baixar: ${err.message}`);
  } finally {
    setLoading(null);
  }
}

function MapHeader() {
  const { wmsLayers } = useMap();
  const [loading, setLoading] = useState(null);

  const visible = wmsLayers
    .filter((layer) => layer.visible)
    .sort((a, b) => a.order - b.order);

  const handleDownload = useCallback((fmt) => {
    if (fmt.outputFormat === '__xlsx__') {
      downloadXlsx(visible, setLoading);
      return;
    }

    if (visible.length === 1) {
      downloadDirect(visible[0], fmt);
    } else {
      downloadZipped(visible, fmt, setLoading);
    }
  }, [visible]);

  const handleVisualExport = useCallback((kind) => {
    const filenameBase = visible.length === 1
      ? safeFileName(visible[0].name)
      : 'mapa_atual';

    setLoading(kind);
    captureMapSnapshot(filenameBase)
      .then(({ dataUrl, filenameBase: safeName }) => {
        if (kind === 'Capturar') {
          downloadFile(dataUrl, `${safeName}.png`);
        } else {
          printImageAsPdf(dataUrl, safeName);
        }
      })
      .catch((err) => {
        console.warn('Snapshot export failed:', err);
        alert('Nao foi possivel capturar o mapa atual. Se o mapa base bloquear CORS, tente outra base.');
      })
      .finally(() => {
        setLoading(null);
      });
  }, [visible]);

  if (visible.length === 0) return null;

  const isMixed = visible.length > 1;
  const title = isMixed ? 'Mapa Misto' : visible[0].name;
  const subtitle = isMixed ? visible.map((layer) => layer.name).join(' · ') : null;
  const count = visible.length;

  return (
    <div className="map-header">
      <div className="map-header-info">
        <div className="map-header-title-row">
          {isMixed ? (
            <svg
              className="map-header-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
          ) : (
            <svg
              className="map-header-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
              <line x1="9" y1="3" x2="9" y2="18" />
              <line x1="15" y1="6" x2="15" y2="21" />
            </svg>
          )}
          <span className="map-header-title">{title}</span>
        </div>
        {subtitle && <p className="map-header-subtitle">{subtitle}</p>}
      </div>

      <div className="map-header-actions">
        {VISUAL_EXPORTS.map((action) => {
          const isLoading = loading === action.label;
          return (
            <button
              key={action.label}
              className={`map-header-btn map-header-btn--visual${isLoading ? ' map-header-btn--loading' : ''}`}
              onClick={() => !loading && handleVisualExport(action.label)}
              disabled={!!loading}
              title={
                action.type === 'image'
                  ? 'Capturar o mapa atual como imagem PNG'
                  : 'Abrir um snapshot do mapa atual para salvar em PDF'
              }
            >
              {isLoading ? (
                <span className="map-header-spinner" />
              ) : action.type === 'image' ? (
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-2h6l2 2h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              ) : (
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 2h9l5 5v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="16" y2="17" />
                </svg>
              )}
              <span>{action.label}</span>
            </button>
          );
        })}

        {FORMATS.map((fmt) => {
          const isLoading = loading === fmt.label;
          return (
            <button
              key={fmt.label}
              className={`map-header-btn${isLoading ? ' map-header-btn--loading' : ''}`}
              onClick={() => !loading && handleDownload(fmt)}
              disabled={!!loading}
              title={
                count > 1
                  ? `Baixar ${count} camadas em ${fmt.label} (zip)`
                  : `Baixar ${fmt.label}`
              }
            >
              {isLoading ? (
                <span className="map-header-spinner" />
              ) : (
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
              <span>{fmt.label}</span>
              {count > 1 && <span className="map-header-btn-count">{count}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MapHeader;
